import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCreditPlans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/credit-plans`);
      setPlans(response.data || []);
    } catch (error) {
      toast.error('獲取套餐失敗');
    } finally {
      setLoading(false);
    }
  };

  const savePlan = async () => {
    try {
      if (editingPlan.id && editingPlan.id !== 'new') {
        await axios.put(`${API_URL}/api/admin/credit-plans/${editingPlan.id}`, editingPlan);
        toast.success('套餐已更新');
      } else {
        await axios.post(`${API_URL}/api/admin/credit-plans`, editingPlan);
        toast.success('套餐已創建');
      }
      setShowDialog(false);
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error('保存失敗');
    }
  };

  const deletePlan = async (planId) => {
    if (!confirm('確定要刪除此套餐嗎？')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/credit-plans/${planId}`);
      toast.success('套餐已刪除');
      fetchPlans();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const openEditDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
    } else {
      setEditingPlan({
        id: 'new',
        name: '',
        price: 0,
        currency: 'HKD',
        credits: 0,
        validity_days: 30
      });
    }
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/stats')} className="rounded-xl text-atelier-ink hover:text-white hover:bg-white/5">
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回
            </Button>
            <h1 className="font-display text-3xl font-semibold text-atelier-ink tracking-tight">
              Credit 套餐管理
            </h1>
          </div>
          <Button onClick={() => openEditDialog()} className="rounded-full btn-neon font-semibold">
            <Plus className="w-4 h-4 mr-2" />
            新增套餐
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.filter(p => p.active).map(plan => (
            <div key={plan.id} className="bg-atelier-surface2 rounded-2xl border border-atelier-line p-6">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="font-display text-3xl font-semibold text-atelier-coral tabular-nums mb-1">
                  {plan.currency} ${plan.price}
                </div>
                <p className="text-sm text-atelier-ink">{plan.credits} Credits</p>
                <p className="text-xs text-atelier-mute mt-2">有效期：{plan.validity_days} 天</p>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => openEditDialog(plan)}
                  variant="outline"
                  size="sm"
                  className="flex-1 rounded-lg"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  編輯
                </Button>
                <Button
                  onClick={() => deletePlan(plan.id)}
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Dialog */}
      {showDialog && editingPlan && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border border-neutral-200">
            <h3 className="text-xl font-semibold text-neutral-900 mb-6">
              {editingPlan.id === 'new' ? '新增套餐' : '編輯套餐'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">套餐名稱</label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                  className="rounded-lg"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">價格</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({...editingPlan, price: parseFloat(e.target.value)})}
                    className="rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">幣種</label>
                  <Select
                    value={editingPlan.currency}
                    onValueChange={(val) => setEditingPlan({...editingPlan, currency: val})}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HKD">HKD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="CNY">CNY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Credits 數量</label>
                <Input
                  type="number"
                  value={editingPlan.credits}
                  onChange={(e) => setEditingPlan({...editingPlan, credits: parseInt(e.target.value)})}
                  className="rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">有效期（天）</label>
                <Input
                  type="number"
                  value={editingPlan.validity_days}
                  onChange={(e) => setEditingPlan({...editingPlan, validity_days: parseInt(e.target.value)})}
                  className="rounded-lg"
                />
                <p className="text-xs text-neutral-500 mt-1">購買後 {editingPlan.validity_days} 天內有效</p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  setEditingPlan(null);
                }}
                className="flex-1 rounded-lg"
              >
                取消
              </Button>
              <Button
                onClick={savePlan}
                className="flex-1 rounded-lg bg-gradient-to-r from-primary-600 to-secondary-600 text-white"
              >
                保存
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreditPlans;
